import {
  Alert,
  Button,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core"
import { t } from "i18next"
import { DefaultModal } from "@components/default-modal"
import {
  useGetRoyaltiesSettlement,
  type RoyaltiesCalculationResponse,
  type RoyaltySettlementResponse,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useTranslation } from "react-i18next"
import { getFormattedDate } from "@/utils/get-formatted-date"

interface CalculateRoyaltiesModalProps {
  opened: boolean
  onClose: () => void
  calculation: RoyaltiesCalculationResponse
  yearMonth: {
    year: number
    month: number
  }
}

type ActivityRow = {
  activityId: number
  activityTitle: string
  startTime: string
  totalTicketsCount: number
  royaltyTicketsCount: number
  totalTicketPrice: number
  royaltyTicketPrice: number
  totalTicketFees: number
  royaltyAmount: number
}

const toActivityRows = (
  settlements: RoyaltySettlementResponse[]
): ActivityRow[] => {
  const rowsByActivity = new Map<number, ActivityRow>()

  for (const settlement of settlements) {
    for (const ruleBlock of settlement.rulePositions ?? []) {
      for (const position of ruleBlock.positions ?? []) {
        const activityId = position.activityId
        if (activityId == null) {
          continue
        }

        const row = rowsByActivity.get(activityId) ?? {
          activityId,
          activityTitle: "",
          startTime: "",
          totalTicketsCount: 0,
          royaltyTicketsCount: 0,
          totalTicketPrice: 0,
          royaltyTicketPrice: 0,
          totalTicketFees: 0,
          royaltyAmount: 0,
        }
        row.totalTicketsCount += position.totelTicketsCount ?? 0
        row.activityTitle = position.activityTitle ?? ""
        row.startTime = position.startTime ?? ""
        row.royaltyTicketsCount += position.royaltyTicketsCount ?? 0
        row.totalTicketPrice += position.totalTicketPrice ?? 0
        row.royaltyTicketPrice += position.royaltyTicketPrice ?? 0
        row.totalTicketFees += position.totalTicketFees ?? 0
        row.royaltyAmount += position.royaltyAmount ?? 0
        rowsByActivity.set(activityId, row)
      }
    }
  }

  return Array.from(rowsByActivity.values()).sort(
    (first, second) => first.activityId - second.activityId
  )
}

const toSummary = (settlements: RoyaltySettlementResponse[]) => {
  let settlementCount = 0
  let positionCount = 0
  let netTotal = 0
  let taxTotal = 0
  let grossTotal = 0
  const activityIds = new Set<number>()

  for (const settlement of settlements) {
    let hasPositions = false
    for (const ruleBlock of settlement.rulePositions ?? []) {
      const positions = ruleBlock.positions ?? []
      if (positions.length > 0) {
        hasPositions = true
        positionCount += positions.length
        for (const position of positions) {
          if (position.activityId != null) {
            activityIds.add(position.activityId)
          }
        }
      }
    }

    if (hasPositions) {
      settlementCount++
    }

    netTotal += settlement.netPrice ?? 0
    taxTotal += settlement.taxes ?? 0
    grossTotal += settlement.endPrice ?? 0
  }

  return {
    settlementCount,
    positionCount,
    activityCount: activityIds.size,
    netTotal,
    taxTotal,
    grossTotal,
  }
}

const moneyFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
})

function CalculateRoyaltiesModalContent({
  onClose,
  yearMonth,
  calculation,
}: CalculateRoyaltiesModalProps) {
  const { i18n } = useTranslation()
  const {
    refetch: download,
    isFetching,
    isFetched: isDownloaded,
    isError: isDownloadError,
  } = useGetRoyaltiesSettlement(
    { calculationId: calculation.calculationId, lang: i18n.language },
    { query: { enabled: false, retry: false, gcTime: 0 } }
  )
  const calculationSummary = calculation.settlements
  const result = toSummary(calculationSummary)
  const activityRows = toActivityRows(calculationSummary)

  const handleDownload = async () => {
    try {
      const { data: fileBlob } = await download({ throwOnError: true })
      if (!fileBlob) {
        throw new Error(t("DownloadSettlementFailed"))
      }

      const objectUrl = window.URL.createObjectURL(fileBlob)
      try {
        const link = document.createElement("a")
        link.href = objectUrl
        link.download =
          fileBlob instanceof File
            ? fileBlob.name
            : `Royalties${yearMonth.year}-${String(yearMonth.month).padStart(2, "0")}.docx`
        document.body.appendChild(link)
        link.click()
        link.remove()
      } finally {
        window.URL.revokeObjectURL(objectUrl)
      }
    } catch (error) {
      showErrorNotification(
        error instanceof Error ? error : t("DownloadFailedUnexpected")
      )
    }
  }

  return (
    <Stack gap="md">
      {/* <Alert color="blue" title={t("CalculationScope")}>
        {t("CalculationScopeDescription")}
      </Alert> */}

      <Alert color="green" title={t("Result")}>
        <Stack gap={4}>
          <Text size="sm">
            {t("Settlements")}: {result.settlementCount}
          </Text>
          <Text size="sm">
            {t("RulePositions")}: {result.positionCount}
          </Text>
          <Text size="sm">
            {t("Activities")}: {result.activityCount}
          </Text>
          <Text size="sm">
            {t("Net")}: {moneyFormatter.format(result.netTotal)}
          </Text>
          <Text size="sm">
            {t("Tax")}: {moneyFormatter.format(result.taxTotal)}
          </Text>
          <Text fw={600} size="sm">
            {t("Gross")}: {moneyFormatter.format(result.grossTotal)}
          </Text>
        </Stack>
      </Alert>

      {activityRows.length > 0 && (
        <Alert color="blue" title={t("DetailsPerActivity")}>
          <Stack gap="xs">
            <Text size="sm">{t("DetailsPerActivityDescription")}</Text>

            <ScrollArea>
              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("Activity")}</Table.Th>
                    <Table.Th>{t("TicketsTotal")}</Table.Th>
                    <Table.Th>{t("TicketsRoyalty")}</Table.Th>
                    <Table.Th>{t("RevenueTotal")}</Table.Th>
                    <Table.Th>{t("RevenueRoyalty")}</Table.Th>
                    <Table.Th>{t("FeesTotal")}</Table.Th>
                    <Table.Th>{t("Royalty")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {activityRows.map((row) => (
                    <Table.Tr key={row.activityId}>
                      <Table.Td>
                        {row.activityTitle}{" "}
                        {getFormattedDate(row.startTime, "L LT")}
                      </Table.Td>
                      <Table.Td>{row.totalTicketsCount}</Table.Td>
                      <Table.Td>{row.royaltyTicketsCount}</Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.totalTicketPrice)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.royaltyTicketPrice)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.totalTicketFees)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.royaltyAmount)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td>
                      <Text fw={700}>{t("Sum")}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {activityRows.reduce(
                          (sum, row) => sum + row.totalTicketsCount,
                          0
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {activityRows.reduce(
                          (sum, row) => sum + row.royaltyTicketsCount,
                          0
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.totalTicketPrice,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.royaltyTicketPrice,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.totalTicketFees,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.royaltyAmount,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Alert>
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={isFetching}>
          {t("Close")}
        </Button>
        <Button
          disabled={isDownloaded && !isDownloadError}
          onClick={handleDownload}
          loading={isFetching}
        >
          {t("DownloadSettlement")}
        </Button>
      </Group>
    </Stack>
  )
}
export function CalculateRoyaltiesModal(props: CalculateRoyaltiesModalProps) {
  return (
    <DefaultModal
      opened={props.opened}
      onClose={props.onClose}
      title={t("CalculateRoyalties")}
      size="xl"
    >
      {props.opened && <CalculateRoyaltiesModalContent {...props} />}
    </DefaultModal>
  )
}
